import React, { useState, useEffect } from 'react';
import { InterviewRequestsFacade } from '@/services/interview';
import { InterviewRequestResponse } from '@/services/interview/requests/types';
import InterviewRequestList from './InterviewRequestList';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw, TrendingUp, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const ArchivedRequestsTab: React.FC = () => {
  const [archivedRequests, setArchivedRequests] = useState<InterviewRequestResponse[]>([]);
  const [statistics, setStatistics] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewingRequest, setViewingRequest] = useState<InterviewRequestResponse | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [archived, stats] = await Promise.all([
        InterviewRequestsFacade.getArchivedInterviewRequests(),
        InterviewRequestsFacade.getInterviewRequestStatistics()
      ]);
      setArchivedRequests(archived);
      setStatistics(stats);
    } catch (error) {
      console.error('Error fetching archived data:', error);
      toast.error('Kunne ikke laste arkiverte forespørsler');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUnarchive = async (requestId: string) => {
    try {
      const result = await InterviewRequestsFacade.unarchiveInterviewRequest(requestId);
      if (result.success) {
        toast.success('Forespørsel gjenopprettet');
        fetchData();
      } else {
        toast.error(result.error || 'Kunne ikke gjenopprette forespørsel');
      }
    } catch (error) {
      console.error('Error unarchiving request:', error);
      toast.error('Kunne ikke gjenopprette forespørsel');
    }
  };

  const handleDelete = async (requestId: string) => {
    fetchData();
  };

  const handleViewRequest = (request: InterviewRequestResponse) => {
    setViewingRequest(request);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Totalt sendt</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statistics.total_sent}</div>
              <p className="text-xs text-muted-foreground">
                Alle forespørsler
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Besvart</CardTitle>
              <CheckCircle2 className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statistics.total_answered}</div>
              <p className="text-xs text-muted-foreground">
                Fullførte intervjuer
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Venter</CardTitle>
              <Clock className="h-4 w-4 text-amber-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statistics.total_pending}</div>
              <p className="text-xs text-muted-foreground">
                Ikke besvart ennå
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Svar-rate</CardTitle>
              <XCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statistics.answer_rate}%</div>
              <p className="text-xs text-muted-foreground">
                Andel besvart
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Header with refresh button */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Arkiverte forespørsler</h2>
          <p className="text-sm text-gray-600">
            {archivedRequests.length} arkiverte forespørsler
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchData}
          className="flex items-center gap-1"
        >
          <RefreshCw className="w-4 h-4" />
          Oppdater
        </Button>
      </div>

      {/* Archived Requests List */}
      {archivedRequests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">Ingen arkiverte forespørsler ennå.</p>
          </CardContent>
        </Card>
      ) : (
        <InterviewRequestList
          interviewRequests={archivedRequests}
          onCreateNew={() => {}}
          onDeleteSuccess={handleDelete}
          onViewRequest={handleViewRequest}
          onArchive={handleUnarchive}
          showUnarchive={true}
        />
      )}
    </div>
  );
};

export default ArchivedRequestsTab;
